import * as focusTrap from 'focus-trap';
import Facets from '../components/Facets';
import ProductItem from '../components/product/ProductItem';
import Splide from '@splidejs/splide';
import { unitlessBreakpoints } from '../helpers/breakpoints';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  productItems: '[data-product-item]',
  filterForm: '[data-filter-form]',
  facetsDrawer: '[data-facets-drawer]',
  facetsDrawerPanel: '[data-facets-drawer-panel]',
  facetsDrawerBackdrop: '[data-facets-drawer-backdrop]',
  facetsDrawerToggle: '[data-facets-drawer-toggle]',
  facetsDrawerClose: '[data-facets-drawer-close]',
  quickFilters: '[data-quick-filters]',
  quickFilterButton: '[data-quick-filter]',
  seriesSlider: '[data-series-slider]',
};

export default class MainCollection {
  constructor(section) {
    this.el = section.el;
    this.quickFiltersSlider = null;
    const productItems = this.el.querySelectorAll(selectors.productItems);

    [...productItems].forEach(productItem => {
      new ProductItem(productItem);
    });

    Facets();

    this._initFacetsDrawer();
    this._initQuickFilters();
  }

  _initFacetsDrawer = () => {
    this.facetsDrawer = this.el.querySelector(selectors.facetsDrawer);
    if (!this.facetsDrawer) {
      return;
    }

    this.facetsDrawerPanel = this.facetsDrawer.querySelector(
      selectors.facetsDrawerPanel,
    );
    this.facetsDrawerBackdrop = this.facetsDrawer.querySelector(
      selectors.facetsDrawerBackdrop,
    );

    this.focusTrap = focusTrap.createFocusTrap(selectors.facetsDrawer, {
      clickOutsideDeactivates: true,
    });

    const toggleButton = this.el.querySelector(selectors.facetsDrawerToggle);
    if (toggleButton) {
      toggleButton.addEventListener('click', this.openFacetsDrawer);
    }

    const closeButtons = this.facetsDrawer.querySelectorAll(
      selectors.facetsDrawerClose,
    );
    closeButtons.forEach(button => {
      button.addEventListener('click', this.closeFacetsDrawer);
    });

    if (this.facetsDrawerBackdrop) {
      this.facetsDrawerBackdrop.addEventListener(
        'click',
        this.closeFacetsDrawer,
      );
    }

    // Check for openFilters query parameter and open drawer if present
    this._checkForOpenFiltersParam();
  };

  _checkForOpenFiltersParam = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openFilters') === 'true') {
      // Open the drawer
      this.openFacetsDrawer();

      // Remove the parameter from URL without page reload
      urlParams.delete('openFilters');
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? '?' + urlParams.toString() : '') +
        window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
  };

  openFacetsDrawer = () => {
    this.facetsDrawer.classList.add('is-open');
    disablePageScroll();
    this.focusTrap.activate();
  };

  closeFacetsDrawer = () => {
    this.facetsDrawer.classList.remove('is-open');
    enablePageScroll();
    this.focusTrap.deactivate();
  };

  _initQuickFilters = () => {
    const quickFiltersContainer = this.el.querySelector(selectors.quickFilters);
    if (!quickFiltersContainer) {
      return;
    }

    // Initialize Splide slider for quick filters
    this._initQuickFiltersSlider();

    const quickFilterButtons = quickFiltersContainer.querySelectorAll(
      selectors.quickFilterButton,
    );
    quickFilterButtons.forEach(button => {
      button.addEventListener('click', this._onQuickFilterClick);
    });

    // Sync button states with checkboxes on init
    this._syncQuickFilterStates();

    // Listen for filter updates to sync states
    const observer = new MutationObserver(() => {
      this._syncQuickFilterStates();
    });

    const filterForm = document.querySelector(selectors.filterForm);
    if (filterForm) {
      observer.observe(filterForm, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['checked'],
      });
    }
  };

  _initQuickFiltersSlider = () => {
    const sliderEl = this.el.querySelector(selectors.seriesSlider);
    if (!sliderEl) {
      return;
    }

    this.quickFiltersSlider = new Splide(sliderEl, {
      type: 'slide',
      autoWidth: true,
      focus: 'left',
      arrows: false,
      pagination: false,
      gap: '0.625rem',
      mediaQuery: 'min',
      breakpoints: {
        [unitlessBreakpoints.md]: {
          destroy: true,
        },
      },
    });

    this.quickFiltersSlider.mount();
  };

  _syncQuickFilterStates = () => {
    const quickFilterButtons = this.el.querySelectorAll(
      selectors.quickFilterButton,
    );
    quickFilterButtons.forEach(button => {
      const checkboxId = button.dataset.checkboxId;
      const checkbox = document.getElementById(checkboxId);
      if (checkbox) {
        const isChecked = checkbox.checked;
        if (isChecked) {
          button.classList.add('pill--active');
          button.setAttribute('aria-pressed', 'true');
        } else {
          button.classList.remove('pill--active');
          button.setAttribute('aria-pressed', 'false');
        }
      }
    });
  };

  _onQuickFilterClick = event => {
    const button = event.currentTarget;
    const checkboxId = button.dataset.checkboxId;

    // Find the checkbox in the facets drawer
    const checkbox = document.getElementById(checkboxId);
    if (!checkbox) {
      return;
    }

    // Toggle the checkbox
    checkbox.checked = !checkbox.checked;

    // Update button active state
    if (checkbox.checked) {
      button.classList.add('pill--active');
      button.setAttribute('aria-pressed', 'true');
    } else {
      button.classList.remove('pill--active');
      button.setAttribute('aria-pressed', 'false');
    }

    // Trigger input event on the form to auto-submit
    const form = checkbox.closest('form');
    if (form) {
      const inputEvent = new Event('input', { bubbles: true });
      checkbox.dispatchEvent(inputEvent);
    }
  };
}
